import { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./EnergyInsight.css";
import CustomTooltip from "../../../common/CustomTooltip";
import { useT } from "../../../../i18n";
import {
  AXIS_TICK,
  CHART_HEIGHT,
  CHART_MARGIN,
  GRID_STROKE,
  Y_AXIS_WIDTH,
  unitLabel,
} from "./chartStyle";

// 데이터는 에너지 탭이 /api/dashboard/{farmId}/overview 로 한 번에 받아 내려준다.
// 이 컴포넌트는 그리기만 한다.
//
// 장치가 1대면 daily 를, 여러 대면 series([{ key, label, daily }])를 받는다.
// series 가 둘 이상이면 탭 위에 장치 선택 줄이 붙는다(fog 구성은 그룹 합계가 맨 앞).
// 높이 · 축 스타일은 타임라인 차트와 같다(chartStyle.js).

const formatDateLabel = (isoDate) => {
  if (!isoDate) return "";
  const parts = isoDate.split("-");
  if (parts.length < 3) return isoDate;
  return `${parts[1]}/${parts[2]}`;
};

// label 은 i18n 키. 렌더 때 t() 로 푼다.
// 저장(배터리 % 일평균) 탭은 뺐다 — 배터리는 카드마다 지금 값이 보이고, 일평균 %는 추이로 볼 만한 값이 아니다.
const TABS = [
  { key: "generation", labelKey: "energy.monitoring.tab.generation", field: "totalGenKwh", unit: "kWh", color: "#4ad4a8" },
  { key: "consumption", labelKey: "energy.monitoring.tab.consumption", field: "totalConsKwh", unit: "kWh", color: "#FFA500" },
];

export default function EnergyMonitoring({
  daily = [],
  series = null,
  // 안내 문구 표기용. 일 집계(total_gen_kwh)는 이미 태양광+풍력 합계라
  // 폴/큐브가 동일하므로 그래프 로직 자체는 갈리지 않는다.
  statusMessage,
  isLoading = false,
  // 기간(1일/7일/30일) · 갱신 줄. 이 차트만 기간을 타므로 탭 맨 위가 아니라 여기 둔다.
  toolbar = null,
  showEmptyChart = false,
  from = null,
  to = null,
}) {
  const t = useT();
  const [selectedTab, setSelectedTab] = useState("generation");
  const [selectedSeries, setSelectedSeries] = useState(null);
  const tab = TABS.find((item) => item.key === selectedTab) ?? TABS[0];
  const tabLabel = t(tab.labelKey);
  const seriesName = t("energy.monitoring.series.measured", { label: tabLabel });

  const hasSeries = Array.isArray(series) && series.length > 0;
  const activeSeries = hasSeries
    ? series.find((item) => item.key === selectedSeries) ?? series[0]
    : null;
  const rows = useMemo(
    () => (activeSeries ? activeSeries.daily ?? [] : daily),
    [activeSeries, daily]
  );

  // Empty dates preserve the deployed chart layout without inventing measurements.
  const chartRows = useMemo(() => {
    if (rows.length || !showEmptyChart) return rows;
    const dates = [];
    for (let day = Date.parse(from); day <= Date.parse(to); day += 86400000) {
      dates.push({ statDate: new Date(day).toISOString().slice(0, 10) });
    }
    return dates;
  }, [rows, showEmptyChart, from, to]);

  const data = useMemo(
    () =>
      chartRows.map((item) => ({
        date: formatDateLabel(item.statDate),
        historical: item[tab.field] ?? null,
      })),
    [chartRows, tab.field]
  );

  const total = useMemo(
    () => rows.reduce((sum, item) => sum + (Number(item[tab.field]) || 0), 0),
    [rows, tab.field]
  );

  return (
    <div className="panel ei-panel">
      <div className="panel-title">{t("energy.monitoring.title")}</div>
      {toolbar}

      {hasSeries && series.length > 1 && (
        <div className="ei-device-select" role="tablist">
          {series.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`ei-device-pill${activeSeries.key === item.key ? " active" : ""}`}
              aria-pressed={activeSeries.key === item.key}
              onClick={() => setSelectedSeries(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {statusMessage ? (
        <div className="ei-empty-message">{statusMessage}</div>
      ) : isLoading && !showEmptyChart ? (
        <div className="ei-empty-message">{t("energy.monitoring.loading")}</div>
      ) : rows.length === 0 && !showEmptyChart ? (
        <div className="ei-empty-message">{t("energy.monitoring.empty")}</div>
      ) : (
        <>
          <div className="ei-tabs">
            {TABS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`ei-tab ${selectedTab === item.key ? "active" : ""}`}
                onClick={() => setSelectedTab(item.key)}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </div>

          <div className="ei-graph-container">
            <div className="ei-graph">
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <LineChart data={data} margin={CHART_MARGIN}>
                  <CartesianGrid stroke={GRID_STROKE} vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                    minTickGap={16}
                  />
                  <YAxis
                    domain={[0, "auto"]}
                    interval="preserveStartEnd"
                    width={Y_AXIS_WIDTH}
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    label={unitLabel(tab.unit)}
                  />
                  <Tooltip content={<CustomTooltip unitOverride={tab.unit} decimals={2} />} />
                  <Line
                    type="monotone"
                    dataKey="historical"
                    stroke={tab.color}
                    strokeWidth={2}
                    dot={rows.length === 1 ? { r: 2.5, strokeWidth: 0, fill: tab.color } : false}
                    name={seriesName}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="ei-split-legend ei-series-foot">
              <span className="ei-legend-key">
                <i style={{ backgroundColor: tab.color }} />
                {seriesName}
              </span>
              <span className="ei-series-total">
                {t("energy.monitoring.cumulative")}{" "}
                <b>
                  {rows.length ? total.toFixed(1) : "–"} {tab.unit}
                </b>
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
